/**
 * Editable i18n namespace whitelist.
 *
 * Tenants can override texts only in **public-facing** namespaces.
 * Mirrored on the backend in
 * ``apps/api/app/services/i18n_editable_namespaces.py`` — when you
 * change one, change the other.
 */

export const EDITABLE_NAMESPACES = new Set<string>([
  "common",
  "contact",
  "how_it_works",
  "materials",
  "pricing",
  "public",
  "track",
]);

/**
 * Promise-bearing keys (delivery times, material counts, "seconds to
 * price", …) that the FE renders from ``landing_facts``. Tenants
 * cannot override these — letting them edit "Cena do 12 sekund"
 * without a BE field to measure against re-introduces the bug that
 * shipped Q2/2026.
 *
 * Encoded as ``"ns|key"`` strings (Set is faster than tuple lookup in
 * JS). Mirrored on the backend in
 * ``apps/api/app/services/site_customization.py::LOCKED_I18N_KEYS``.
 */
export const LOCKED_I18N_KEYS = new Set<string>([
  // Rozhodnutí vlastníka 2026-09-28: bez „Powered by Forgmatic" je jen
  // Enterprise, a to přes nastavení značky, ne přepsáním textu (KAN-1816).
  "common|footer.poweredBy",
  "common|home.features.heading",
  "common|home.features.lead",
  "common|home.features.price.title",
  "common|home.features.price.body",
  "common|home.features.delivery.title",
  "common|home.features.delivery.titleDays",
  "common|home.features.delivery.body",
  "common|home.features.delivery.bodyFallback",
  "common|home.features.delivery.tierDays",
  "common|home.features.delivery.joiner",
  "common|home.hero.featureMaterials",
  "common|home.hero.featureExpress",
  "common|home.hero.featureShipping",
  "common|home.trust.items.materials",
  "common|home.trust.items.express",
  "common|home.trust.items.shipping",
  "common|home.trust.items.eu",
  "common|home.highlights.materials_one",
  "common|home.highlights.materials_few",
  "common|home.highlights.materials_many",
  "common|home.highlights.materials_other",
  "common|home.highlights.materialsWithNames_one",
  "common|home.highlights.materialsWithNames_few",
  "common|home.highlights.materialsWithNames_many",
  "common|home.highlights.materialsWithNames_other",
  "common|home.highlights.tierDays_one",
  "common|home.highlights.tierDays_few",
  "common|home.highlights.tierDays_many",
  "common|home.highlights.tierDays_other",
  "common|home.highlights.paymentCard",
  "common|home.highlights.shipping",
  "common|home.highlights.minimumOrderTopup",
]);

export function isLockedI18nKey(ns: string, key: string): boolean {
  return LOCKED_I18N_KEYS.has(`${ns}|${key}`);
}

/**
 * Namespaces the PLATFORM admin may write. Mirror of the backend
 * ``platform_site_customization.PLATFORM_EDITABLE_NAMESPACES``.
 *
 * Superset of the tenant set: ``saas`` holds the acquisition funnel
 * (``/for-providers``, ``/for-business``, ``/pricing-plans``,
 * ``/sign-up-shop``) which only the platform shell renders. Tenants
 * keep the smaller ``EDITABLE_NAMESPACES`` — a tenant has no route that
 * renders ``saas``, so writing it would be a dead write at best.
 */
export const PLATFORM_EDITABLE_NAMESPACES = new Set<string>([
  ...EDITABLE_NAMESPACES,
  "saas",
]);

/**
 * Platform-shell allow-list of KEY PREFIXES within an editable
 * namespace. Mirror of the backend scope.
 *
 * ``common`` is shared with the tenant storefront, so only the
 * ``platformLanding.*`` subtree is persistable — Header / Footer
 * strings under ``common`` render on the platform shell too, but the
 * platform overrides blob is scoped to marketing copy. ``saas`` is
 * platform-only from top to bottom, so the empty prefix admits the
 * whole namespace.
 *
 * The marker post-processor skips wrapping outside this list, so the
 * platform admin never sees a pencil that would 400 on save.
 */
export const PLATFORM_EDITABLE_KEY_PREFIXES: ReadonlyArray<[string, string]> = [
  ["common", "platformLanding."],
  ["saas", ""],
];

export function isPlatformEditableKey(ns: string, key: string): boolean {
  for (const [allowedNs, prefix] of PLATFORM_EDITABLE_KEY_PREFIXES) {
    if (ns === allowedNs && key.startsWith(prefix)) return true;
  }
  return false;
}

/**
 * Platform-shell locked keys — copy that names a hard platform commitment
 * (trial length, instant-quote SLA, free-tier inquiry cap). Mirror of
 * the backend ``PLATFORM_LOCKED_I18N_KEYS``. The pencil is skipped for
 * these so platform staff can’t rewrite "14 days free" to a number the
 * platform doesn’t actually offer.
 *
 * The numbers live in the copy itself. A structured source once held
 * them (``platform_facts``, deleted in KAN-1226) and these keys were
 * interpolation templates; one number that changed once did not pay
 * for a config surface, so changing one is a deploy — and this lock is
 * what keeps it from being a form field again by accident.
 */
export const PLATFORM_LOCKED_I18N_KEYS = new Set<string>([
  // Billing and availability terms mirror the backend lock.
  "common|platformLanding.features.items.capacity.body",
  "common|platformLanding.features.items.branding.body",
  "common|platformLanding.faq.items.card.a",
  "common|platformLanding.faq.items.ownership.a",
  "common|platformLanding.faq.items.manual.a",
  "common|platformLanding.faq.items.delivery.a",
  "common|platformBrandingNotice",
  "common|platformTryout.preview.sub",
  "saas|pricingPlans.freeForever",
  "saas|pricingPlans.priceUnit.free",
  "saas|pricingPlans.lead",
  "saas|pricingPlans.explainer.lead",
  "saas|pricingPlans.explainer.fee.note",
  "saas|pricingPlans.explainer.total.note",
  "saas|pricingPlans.faq.transactionFee.a",
  "saas|pricingPlans.faq.downgrade.a",
  "saas|pricingPlans.finalCta.lead",
  "saas|orgsVerify.card.zeroLead",
  "saas|platformHowItWorks.customer.lead",
  "saas|platformHowItWorks.customer.items.price.body",

  // Same trial length, second page. This answer used to spell the number
  // out ("14denní"), so changing ``trial_days`` moved the landing and
  // left the pricing page claiming the old figure.
  "saas|pricingPlans.faq.trial.a",
  // KAN-1684 — months free on annual billing, derived from the plans'
  // prices (``savedMonths``). Typed by hand it would outlive the next
  // price change.
  "saas|pricingPlans.faq.billingCycle.a_one",
  "saas|pricingPlans.faq.billingCycle.a_few",
  "saas|pricingPlans.faq.billingCycle.a_other",
  "saas|pricingPlans.faq.billingCycle.aNoDiscount",
  // Median hours to a new shop's first price, plus the sample it is
  // taken over. ``platform_measurements`` has no admin write path — an
  // admin able to edit these strings could improve the measurement by
  // typing, which is the one thing a measurement must not allow.
  "saas|platformHowItWorks.setup.timeToPrice",
  "saas|platformHowItWorks.setup.timeToPriceSample_one",
  "saas|platformHowItWorks.setup.timeToPriceSample_few",
  "saas|platformHowItWorks.setup.timeToPriceSample_other",
  // Derived from ``GET /processes`` — the count must never be typed by
  // hand next to the list it claims to describe.
  "common|platformLanding.segments.liveCount_one",
  "common|platformLanding.segments.liveCount_few",
  "common|platformLanding.segments.liveCount_many",
  "common|platformLanding.segments.liveCount_other",
  "common|platformLanding.seo.description",
  "common|platformLanding.hero.subtitle",
  // KAN-1944 — the free tier's commission rate, the tier that opens a
  // custom domain and the tryout's step counts are all read from data
  // (``/public/plans``, the wizard rails). Free text here would let them drift.
  "common|platformLanding.hero.terms",
  "common|platformLanding.hero.termsNoFee",
  "common|platformLanding.hero.termsFallback",
  "common|platformLanding.steps.items.connect.domainFrom",
  "common|platformLanding.steps.items.connect.domainAll",
  "common|platformTryout.lead_one",
  "common|platformTryout.lead_few",
  "common|platformTryout.lead_many",
  "common|platformTryout.lead_other",
  "saas|signUpShop.fields.slug.domainFrom",
  "saas|signUpShop.fields.slug.domainAll",
  "common|platformLanding.finalCta.body",
  "common|platformLanding.finalCta.primary",
  "common|platformLanding.faq.items.pricing.a",
  "common|platformLanding.faq.items.pricing.aUnlimited",
]);

export function isPlatformLockedI18nKey(ns: string, key: string): boolean {
  return PLATFORM_LOCKED_I18N_KEYS.has(`${ns}|${key}`);
}
